// Panneau Mensurations pour le Dashboard web.
// Trois vues :
//   - Liste chronologique des mesures (édition inline + suppression)
//   - Courbe d'évolution par mesure (SVG minimal, pas de dépendance)
//   - Bouton "+" pour créer une nouvelle mesure (formulaire modal simple)
//
// Ordre des 12 mesures fixé côté services/authService.js (MENSURATION_LABELS).
// Chaque mesure est stockée en string ("" = pas renseigné).
import React, { useEffect, useMemo, useState } from "react";
import {
	listMensurations,
	upsertMensuration,
	deleteMensuration,
	MENSURATION_LABELS,
} from "../services/authService";

const EMPTY_DATA = () => Array(12).fill("");

const formatDate = (ts) => {
	if (!ts) return "";
	const d = new Date(Number(ts));
	return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
};

// Vérifie qu'une chaîne est un nombre positif utilisable.
const isNum = (v) => v !== "" && v != null && !isNaN(parseFloat(v)) && parseFloat(v) > 0;

// ─── Formulaire modal d'ajout/édition ──────────────────────────
function EditorModal({ initial, onClose, onSaved }) {
	const [date, setDate] = useState(() => {
		const d = new Date(initial?.date ? Number(initial.date) : Date.now());
		return d.toISOString().slice(0, 10);
	});
	const [values, setValues] = useState(() => {
		const arr = EMPTY_DATA();
		if (initial?.data) {
			for (let i = 0; i < 12; i++) arr[i] = initial.data[i] || "";
		}
		return arr;
	});
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	const handleSave = async () => {
		setSaving(true);
		setError("");
		const timestamp = new Date(date + "T12:00:00").getTime();
		const result = await upsertMensuration({
			id: initial?.id,
			localKey: initial?.localKey,
			date: timestamp,
			data: values,
		});
		setSaving(false);
		if (result.success) {
			onSaved(result.id);
		} else {
			setError(result.error || "Erreur");
		}
	};

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			<div className="mens-modal" onClick={(e) => e.stopPropagation()}>
				<div className="mens-modal-header">
					<h3>{initial?.id > 0 ? "Modifier la mensuration" : "Nouvelle mensuration"}</h3>
					<button className="mens-modal-close" onClick={onClose} aria-label="Fermer">×</button>
				</div>

				<label className="mens-modal-label">
					Date
					<input
						type="date"
						value={date}
						onChange={(e) => setDate(e.target.value)}
						className="mens-modal-date"
					/>
				</label>

				<div className="mens-modal-grid">
					{MENSURATION_LABELS.map((label, i) => (
						<label key={i} className="mens-modal-field">
							<span>{label}</span>
							<div className="mens-modal-input-wrap">
								<input
									type="number"
									step="0.1"
									value={values[i]}
									onChange={(e) => {
										const next = [...values];
										next[i] = e.target.value;
										setValues(next);
									}}
									placeholder=""
								/>
								<span className="mens-modal-unit">cm</span>
							</div>
						</label>
					))}
				</div>

				{error ? <div className="mens-modal-error">{error}</div> : null}

				<div className="mens-modal-actions">
					<button className="mens-btn ghost" onClick={onClose} disabled={saving}>Annuler</button>
					<button className="mens-btn primary" onClick={handleSave} disabled={saving}>
						{saving ? "Enregistrement..." : "Enregistrer"}
					</button>
				</div>
			</div>
		</div>
	);
}

// ─── Mini-chart SVG pour évolution d'UNE mesure ───────────────
function EvolutionChart({ points, color = "#30a49a" }) {
	// points : [{ ts, value }] triés chronologiquement
	if (!points || points.length < 2) {
		return (
			<div className="mens-chart-empty">
				Pas assez de données pour tracer une courbe (au moins 2 mesures).
			</div>
		);
	}

	const W = 480, H = 180, PAD = 24;
	const values = points.map((p) => p.value);
	const timestamps = points.map((p) => p.ts);
	const minV = Math.min(...values);
	const maxV = Math.max(...values);
	const range = maxV - minV || 1;
	const tsMin = Math.min(...timestamps);
	const tsMax = Math.max(...timestamps);
	const tsRange = tsMax - tsMin || 1;

	const xFor = (ts) => PAD + ((ts - tsMin) / tsRange) * (W - PAD * 2);
	const yFor = (v) => H - PAD - ((v - minV) / range) * (H - PAD * 2);

	const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${xFor(p.ts)} ${yFor(p.value)}`).join(" ");

	return (
		<svg viewBox={`0 0 ${W} ${H}`} className="mens-chart-svg" preserveAspectRatio="xMidYMid meet">
			<line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
			<line x1={PAD} y1={PAD} x2={PAD} y2={H - PAD} stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
			<path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
			{points.map((p, i) => (
				<circle key={i} cx={xFor(p.ts)} cy={yFor(p.value)} r="3.5" fill={color} />
			))}
			<text x={PAD - 4} y={PAD + 4} fontSize="10" fill="rgba(255,255,255,0.55)" textAnchor="end">{maxV.toFixed(1)}</text>
			<text x={PAD - 4} y={H - PAD + 4} fontSize="10" fill="rgba(255,255,255,0.55)" textAnchor="end">{minV.toFixed(1)}</text>
		</svg>
	);
}

// ─── Composant principal ──────────────────────────────────────
export default function MensurationsPanel() {
	const [mensurations, setMensurations] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [editorOpen, setEditorOpen] = useState(false);
	const [editorInitial, setEditorInitial] = useState(null);
	const [selectedMeasureIdx, setSelectedMeasureIdx] = useState(6); // "Épaules" par défaut

	const refresh = async () => {
		setLoading(true);
		const r = await listMensurations();
		setLoading(false);
		if (r.success) setMensurations(r.mensurations);
		else setError(r.error || "Erreur");
	};

	useEffect(() => { refresh(); }, []);

	// Points pour la courbe d'évolution de la mesure sélectionnée
	const chartPoints = useMemo(() => {
		return mensurations
			.filter((m) => isNum(m.data?.[selectedMeasureIdx]))
			.map((m) => ({ ts: Number(m.date), value: parseFloat(m.data[selectedMeasureIdx]) }))
			.sort((a, b) => a.ts - b.ts);
	}, [mensurations, selectedMeasureIdx]);

	const openCreate = () => {
		setEditorInitial(null);
		setEditorOpen(true);
	};

	const openEdit = (m) => {
		setEditorInitial(m);
		setEditorOpen(true);
	};

	const handleDelete = async (id) => {
		if (!window.confirm("Supprimer cette mensuration ?")) return;
		const r = await deleteMensuration(id);
		if (r.success) {
			setMensurations((prev) => prev.filter((m) => m.id !== id));
		}
	};

	return (
		<div className="dashboard-tab-content">
			<div className="mens-header">
				<div>
					<h2 className="mens-title">Mensurations</h2>
					<p className="mens-subtitle">Suis l'évolution de tes mesures corporelles.</p>
				</div>
				<button className="mens-btn primary" onClick={openCreate}>
					+ Nouvelle mesure
				</button>
			</div>

			{loading ? (
				<div className="mens-loading">Chargement...</div>
			) : error ? (
				<div className="mens-error">{error}</div>
			) : mensurations.length === 0 ? (
				<div className="mens-empty">
					<div className="mens-empty-icon">📏</div>
					<h3>Aucune mensuration enregistrée</h3>
					<p>Enregistre ta première mesure pour suivre ton évolution.</p>
					<button className="mens-btn primary" onClick={openCreate}>
						Ajouter une mensuration
					</button>
				</div>
			) : (
				<>
					{/* Évolution */}
					<div className="mens-section">
						<div className="mens-section-header">
							<h3>Évolution</h3>
							<select
								value={selectedMeasureIdx}
								onChange={(e) => setSelectedMeasureIdx(parseInt(e.target.value, 10))}
								className="mens-select"
							>
								{MENSURATION_LABELS.map((label, i) => (
									<option key={i} value={i}>{label}</option>
								))}
							</select>
						</div>
						<EvolutionChart points={chartPoints} />
					</div>

					{/* Timeline */}
					<div className="mens-section">
						<h3>Historique</h3>
						<div className="mens-timeline">
							{mensurations.map((m) => {
								const filledCount = (m.data || []).filter(isNum).length;
								return (
									<div key={m.id} className="mens-row">
										<div className="mens-row-header">
											<div>
												<div className="mens-row-date">{formatDate(m.date)}</div>
												<div className="mens-row-count">{filledCount} mesure{filledCount > 1 ? "s" : ""}</div>
											</div>
											<div className="mens-row-actions">
												<button className="mens-btn ghost small" onClick={() => openEdit(m)}>Modifier</button>
												<button className="mens-btn danger small" onClick={() => handleDelete(m.id)}>Supprimer</button>
											</div>
										</div>
										<div className="mens-row-grid">
											{MENSURATION_LABELS.map((label, i) => {
												const v = m.data?.[i];
												if (!isNum(v)) return null;
												return (
													<div key={i} className="mens-row-cell">
														<span className="mens-cell-label">{label}</span>
														<span className="mens-cell-value">{parseFloat(v).toFixed(1)} <em>cm</em></span>
													</div>
												);
											})}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</>
			)}

			{editorOpen && (
				<EditorModal
					initial={editorInitial}
					onClose={() => setEditorOpen(false)}
					onSaved={async () => {
						setEditorOpen(false);
						await refresh();
					}}
				/>
			)}
		</div>
	);
}
